import React, { Suspense, useState, useEffect, useRef } from 'react';
import { Canvas, useThree, useFrame } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import { useSiteStore } from '../../store/siteStore';
import TowerModel from './TowerModel';
import MountingPoints from './MountingPoints';
import Equipment3D from './Equipment3D';
import StayWires from './StayWires';
import SiteListSidebar from './SiteListSidebar';
import SiteEditModal from './SiteEditModal';
import EquipmentTooltip from './EquipmentTooltip';
import { CompassTracker, CompassDisplay } from './Compass';
import { Trash2, ChevronDown, ChevronUp, ArrowLeft } from 'lucide-react';
import * as THREE from 'three';
import { ChevronRight } from 'lucide-react';

// Component inside Canvas to track position and update external state
const PositionTracker: React.FC<{ onPositionUpdate: (pos: { x: number; y: number } | null) => void }> = React.memo(({ onPositionUpdate }) => {
  const { currentSite, selectedEquipment } = useSiteStore();
  const { camera, gl } = useThree();
  const worldPositionRef = useRef(new THREE.Vector3());
  const vectorRef = useRef(new THREE.Vector3());

  useFrame(() => {
    if (!selectedEquipment || !currentSite) {
      onPositionUpdate(null);
      return;
    }

    const mountingPoint = currentSite.tower.mountingPoints.find(
      mp => mp.id === selectedEquipment.mountingPointId
    );

    if (!mountingPoint) {
      onPositionUpdate(null);
      return;
    }

    const angle = (mountingPoint.angle * Math.PI) / 180;
    const towerRadius = 0.3;
    const x = Math.cos(angle) * towerRadius;
    const z = Math.sin(angle) * towerRadius;
    const y = mountingPoint.height;

    worldPositionRef.current.set(x, y, z);
    vectorRef.current.copy(worldPositionRef.current).project(camera);
    
    const rect = gl.domElement.getBoundingClientRect();
    const widthHalf = gl.domElement.clientWidth / 2;
    const heightHalf = gl.domElement.clientHeight / 2;

    const screenPos = {
      x: rect.left + (vectorRef.current.x * widthHalf) + widthHalf,
      y: rect.top + (-(vectorRef.current.y * heightHalf) + heightHalf)
    };

    onPositionUpdate(screenPos);
  });

  return null;
});

const TowerView3D: React.FC<{ onBack?: () => void }> = ({ onBack }) => {
  const { currentSite, isSiteListOpen, setSiteListOpen, setSelectedEquipment, selectedEquipment, setHoveredEquipmentId, removeEquipment } = useSiteStore();
  const [tooltipPosition, setTooltipPosition] = useState<{ x: number; y: number } | null>(null);
  const [compassRotation, setCompassRotation] = useState<number>(0);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [hoveredStayWireSet, setHoveredStayWireSet] = useState<number | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  // Load saved expanded state from localStorage, default to true
  const [isStayWiresExpanded, setIsStayWiresExpanded] = useState(() => {
    const saved = localStorage.getItem('siteInfo_stayWiresExpanded');
    return saved !== null ? saved === 'true' : true;
  });
  const [isEquipmentExpanded, setIsEquipmentExpanded] = useState(() => {
    const saved = localStorage.getItem('siteInfo_equipmentExpanded');
    return saved !== null ? saved === 'true' : true;
  });
  const sidebarRef = useRef<HTMLDivElement>(null);
  
  // Debug: Log when selectedEquipment changes
  useEffect(() => {
    if (selectedEquipment) {
      console.log('Equipment selected in TowerView3D:', selectedEquipment.id, selectedEquipment.equipment.name);
    }
  }, [selectedEquipment]);

  // Save expanded state to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('siteInfo_stayWiresExpanded', String(isStayWiresExpanded));
  }, [isStayWiresExpanded]);

  useEffect(() => {
    localStorage.setItem('siteInfo_equipmentExpanded', String(isEquipmentExpanded));
  }, [isEquipmentExpanded]);

  // Group stay wires by height and distance (unique sets) - MUST be before early return
  const stayWireSets = React.useMemo(() => {
    if (!currentSite || !currentSite.tower.stayWires || currentSite.tower.stayWires.length === 0) return [];
    
    const sets = new Map<string, { height: number; distance: number; length: number; count: number }>();
    currentSite.tower.stayWires.forEach(wire => {
      const key = `${wire.height}-${wire.distance}`;
      if (!sets.has(key)) {
        // Calculate wire length using Pythagorean theorem
        const length = Math.sqrt(wire.height ** 2 + wire.distance ** 2);
        sets.set(key, { height: wire.height, distance: wire.distance, length, count: 1 });
      } else {
        sets.get(key)!.count++;
      }
    });
    
    return Array.from(sets.values());
  }, [currentSite]);

  // Calculate if all sections are collapsed (derived state)
  const allSectionsCollapsed = React.useMemo(() => {
    if (!currentSite) return false;
    const hasStayWires = stayWireSets.length > 0;
    const hasEquipment = currentSite.equipment.length > 0;
    
    // Check if all existing sections are collapsed
    let allCollapsed = true;
    if (hasStayWires && isStayWiresExpanded) allCollapsed = false;
    if (hasEquipment && isEquipmentExpanded) allCollapsed = false;
    
    return allCollapsed;
  }, [isStayWiresExpanded, isEquipmentExpanded, stayWireSets.length, currentSite]);

  const toggleAllSections = () => {
    // If all sections are collapsed, expand them; otherwise collapse them
    const shouldExpand = allSectionsCollapsed;
    setIsStayWiresExpanded(shouldExpand);
    setIsEquipmentExpanded(shouldExpand);
  };

  // Close tooltip when clicking outside - MUST be before early return
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      // Don't close if clicking on the tooltip itself
      if (selectedEquipment && !target.closest('.fixed.z-50')) {
        const canvas = document.querySelector('canvas');
        // Only close if clicking outside the canvas or on the canvas itself (not on a 3D object)
        // But wait a bit to let 3D object clicks process first
        if (canvas && canvas.contains(target)) {
          // Click was on canvas - check if it was on a 3D object
          // If selectedEquipment is still set after a brief delay, it means we clicked on equipment
          setTimeout(() => {
            const current = useSiteStore.getState().selectedEquipment;
            // Only clear if still the same equipment (meaning click was on empty space)
            if (current && current.id === selectedEquipment.id) {
              // Actually, don't clear here - let OrbitControls handle it via onPointerMissed
            }
          }, 50);
        } else {
          // Click was outside canvas - clear selection
          setSelectedEquipment(null);
        }
      }
    };

    if (selectedEquipment) {
      // Use a longer delay to ensure 3D object clicks process first
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 100);
      
      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [selectedEquipment, setSelectedEquipment]);

  // Close sidebar when clicking outside - MUST be before early return
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      
      // Don't close if clicking the navigation button that toggles the sidebar
      if (target.closest('[data-list-toggle-button]')) {
        return;
      }
      
      // Check if click is outside sidebar
      if (isSiteListOpen && sidebarRef.current && !sidebarRef.current.contains(target)) {
        setSiteListOpen(false);
      }
    };

    if (isSiteListOpen) {
      // Small delay to prevent immediate closing when opening
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 100);
      
      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isSiteListOpen, setSiteListOpen]);

  if (!currentSite) {
    return (
      <div className="flex-1 flex relative bg-gradient-to-b from-blue-100 to-gray-100 paper-preview-light">
        {onBack && (
          <button
            onClick={onBack}
            className="absolute top-4 left-4 z-10 flex items-center gap-1.5 px-3 py-1.5 bg-white/90 backdrop-blur-sm rounded-lg shadow-lg text-sm font-medium text-gray-900 hover:bg-white transition-colors"
            title="Back to Sites"
          >
            <ArrowLeft size={16} className="text-gray-900" />
            Sites
          </button>
        )}
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <p className="text-gray-500 mb-4">No site selected</p>
            <button
              onClick={() => setSiteListOpen(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              View Sites
            </button>
          </div>
        </div>
        <div ref={sidebarRef}>
          <SiteListSidebar
            isOpen={isSiteListOpen}
            onClose={() => setSiteListOpen(false)}
            onCreateNew={() => {
              setSiteListOpen(false);
              window.dispatchEvent(new Event('openSiteModal'));
            }}
          />
        </div>
      </div>
    );
  }

  const towerHeight = currentSite.tower.height;
  const cameraDistance = Math.max(towerHeight * 1.5, 20);
  const towerShape = currentSite.tower.shape || 'square';

  // Close tooltip when clicking on canvas background
  const handleCanvasClick = (e: React.MouseEvent) => {
    // Don't handle clicks here - let Equipment3D handle equipment clicks
    // and OrbitControls handle empty space clicks
  };

  return (
    <div className="flex-1 flex relative bg-gradient-to-b from-blue-100 to-gray-100 paper-preview-light">
      {/* Site Edit Modal */}
      <SiteEditModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
      />

      {/* 3D View - Full Width */}
      <div className="flex-1 relative" onClick={handleCanvasClick}>
        {onBack && (
          <button
            onClick={onBack}
            className="absolute top-4 left-4 z-10 flex items-center gap-1.5 px-3 py-1.5 bg-white/90 backdrop-blur-sm rounded-lg shadow-lg text-sm font-medium text-gray-900 hover:bg-white transition-colors"
            title="Back to Sites"
          >
            <ArrowLeft size={16} className="text-gray-900" />
            Sites
          </button>
        )}
        <Canvas
          camera={{ position: [cameraDistance, towerHeight / 2, cameraDistance], fov: 50 }}
          gl={{ 
            antialias: false,
            powerPreference: 'high-performance',
            alpha: false,
            stencil: false,
            depth: true,
            preserveDrawingBuffer: false
          }}
          dpr={[0.75, 1]}
          frameloop="demand"
          shadows={false}
          flat
          linear
        >
          <Suspense fallback={null}>
            {/* Sky/Background */}
            <color attach="background" args={['#f0f9ff']} />
            
            {/* Lighting */}
            <ambientLight intensity={0.9} />
            <directionalLight position={[10, 10, 5]} intensity={1.2} />
            <directionalLight position={[-10, 5, -5]} intensity={0.6} />
            <hemisphereLight args={['#ffffff', '#b0c4de', 0.3]} />

            {/* Ground Plane - Grass */}
            <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]} receiveShadow>
              <planeGeometry args={[50, 50]} />
              <meshStandardMaterial color="#c5e1a5" roughness={0.9} />
            </mesh>

            {/* Oriented group: rotate geometry AND mounting points/equipment together */}
            <group rotation={[0, (currentSite.tower.orientationDeg || 0) * Math.PI / 180, 0]}>
              {/* Tower */}
              <TowerModel
                height={towerHeight}
                crossMemberWidth={currentSite.tower.crossMemberWidth}
                sectionHeight={currentSite.tower.sectionHeight || 3}
                shape={towerShape}
              />

              {/* Stay Wires */}
              {currentSite.tower.stayWires && currentSite.tower.stayWires.length > 0 && (
                <StayWires 
                  stayWires={currentSite.tower.stayWires} 
                  hoveredSet={hoveredStayWireSet !== null ? stayWireSets[hoveredStayWireSet] : null}
                />
              )}

              {/* Mounting Points */}
              <MountingPoints mountingPoints={currentSite.tower.mountingPoints} />

              {/* Equipment */}
              {currentSite.equipment.map((eq) => (
                <Equipment3D key={eq.id} towerEquipment={eq} />
              ))}
            </group>

            {/* Camera Controls */}
            <OrbitControls
              enablePan={true}
              enableZoom={true}
              enableRotate={true}
              minDistance={0.5}
              maxDistance={towerHeight * 3}
              target={[0, towerHeight / 2, 0]}
              enableDamping={false}
              zoomToCursor={true}
              zoomSpeed={2}
              panSpeed={0.5}
              rotateSpeed={1.5}
              onPointerMissed={(e) => {
                // Clear selection when clicking on empty space
                if (e.type === 'click') {
                  setSelectedEquipment(null);
                }
              }}
              makeDefault
            />

            {/* Position tracker inside Canvas */}
            <PositionTracker onPositionUpdate={setTooltipPosition} />
            
            {/* Compass tracker inside Canvas */}
            <CompassTracker onRotationUpdate={setCompassRotation} />
          </Suspense>
        </Canvas>
        
        {/* Compass display outside Canvas */}
        <CompassDisplay rotation={compassRotation} />

        {/* Render tooltip outside Canvas */}
        {selectedEquipment && (
          <EquipmentTooltip
            equipment={selectedEquipment}
            screenPosition={tooltipPosition}
            onClose={() => setSelectedEquipment(null)}
          />
        )}
        

        {/* Tower Info Overlay - Positioned with transition */}
        <div 
          className="absolute top-4 right-4 space-y-2 transition-all duration-300 ease-in-out"
        >
          <div className="bg-white/90 backdrop-blur-sm rounded-lg p-3 shadow-lg min-w-[340px]">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center space-x-2">
                <div className="text-sm font-semibold text-gray-900">{currentSite.name}</div>
                {(stayWireSets.length > 0 || currentSite.equipment.length > 0) && (
                  <button
                    onClick={toggleAllSections}
                    className="p-1 hover:bg-gray-200 rounded transition-colors"
                    title={allSectionsCollapsed ? 'Expand All' : 'Collapse All'}
                  >
                    {allSectionsCollapsed ? (
                      <ChevronDown size={14} className="text-gray-700" />
                    ) : (
                      <ChevronUp size={14} className="text-gray-700" />
                    )}
                  </button>
                )}
              </div>
              <button
                onClick={() => setIsEditModalOpen(true)}
                className="ml-3 px-2 py-1 text-xs bg-blue-600 text-white hover:bg-blue-700 rounded transition-colors"
                title="Edit Site"
              >
                Edit Site
              </button>
            </div>
            <div className="text-xs text-gray-600">
              Height: {towerHeight}m | Cross: {currentSite.tower.crossMemberWidth}mm
            </div>
            <div className="text-xs text-gray-600 mt-1">
              Orientation: {(currentSite.tower.orientationDeg ?? 0)}°
            </div>
            <div className="text-xs text-gray-600 mt-1">
              Equipment: {currentSite.equipment.length}
            </div>
            
            {/* Stay Wire Sets (collapsible) */}
            {stayWireSets.length > 0 && (
              <div className="mt-2 pt-2 border-t border-gray-300">
                <button
                  onClick={() => setIsStayWiresExpanded(!isStayWiresExpanded)}
                  className="w-full flex items-center text-xs font-normal text-gray-600 mb-1 hover:text-gray-800"
                  title={isStayWiresExpanded ? 'Collapse' : 'Expand'}
                  aria-expanded={isStayWiresExpanded}
                >
                  <ChevronRight
                    size={14}
                    className={`mr-1 transition-transform duration-200 ${isStayWiresExpanded ? 'rotate-90' : ''}`}
                  />
                  Stay Wires
                </button>
                <div
                  className={`overflow-hidden transition-all duration-200 ${
                    isStayWiresExpanded ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <div className="space-y-1">
                    {stayWireSets.map((set, index) => (
                      <div
                        key={index}
                        className={`text-xs px-2 py-1 rounded transition-colors select-none ${
                          hoveredStayWireSet === index 
                            ? 'bg-blue-100 text-blue-800' 
                            : 'text-gray-600 hover:bg-gray-100'
                        }`}
                        onMouseEnter={() => setHoveredStayWireSet(index)}
                        onMouseLeave={() => setHoveredStayWireSet(null)}
                      >
                        {set.count}× @ {set.height}m → {set.distance}m ({set.length.toFixed(1)}m length)
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Equipment List (collapsible) */}
            {currentSite.equipment.length > 0 && (
              <div className="mt-2 pt-2 border-t border-gray-300">
                <button
                  onClick={() => setIsEquipmentExpanded(!isEquipmentExpanded)}
                  className="w-full flex items-center text-xs font-normal text-gray-600 mb-1 hover:text-gray-800"
                  title={isEquipmentExpanded ? 'Collapse' : 'Expand'}
                  aria-expanded={isEquipmentExpanded}
                >
                  <ChevronRight
                    size={14}
                    className={`mr-1 transition-transform duration-200 ${isEquipmentExpanded ? 'rotate-90' : ''}`}
                  />
                  Equipment
                </button>
                <div
                  className={`overflow-hidden transition-all duration-200 ${
                    isEquipmentExpanded ? 'max-h-56 opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <div className="space-y-1 pr-1">
                    {currentSite.equipment.map((eq) => {
                      const mp = currentSite.tower.mountingPoints.find(mp => mp.id === eq.mountingPointId);
                      const height = mp ? mp.height : 0;
                      const hasCollision = !!useSiteStore.getState().collisions[eq.id]?.length;
                      return (
                        <div
                          key={eq.id}
                          className="group text-xs px-2 py-1 rounded transition-colors cursor-pointer select-none text-gray-600 hover:bg-gray-100"
                          onMouseEnter={() => setHoveredEquipmentId(eq.id)}
                          onMouseLeave={() => setHoveredEquipmentId(null)}
                          onClick={() => setSelectedEquipment(eq)}
                          title={eq.equipment.name}
                        >
                          <div className="flex items-center justify-between">
                            <span className="flex items-center gap-1 truncate pr-2">
                              {eq.label && (
                                <span className="inline-flex items-center gap-1.5 max-w-[160px] truncate align-middle">
                                  <span className="text-blue-600 font-medium truncate">{eq.label}</span>
                                  <span className="inline-block w-1 h-1 rounded-full bg-black flex-shrink-0 relative top-[2px]" />
                                </span>
                              )}
                              <span className="text-gray-600 font-normal truncate">{eq.equipment.name}</span>
                            </span>
                            <span className="inline-flex items-center">
                              {hasCollision && <span className="w-2 h-2 rounded-full bg-red-500 mr-2" />}
                              <span className="text-[11px] text-gray-600 whitespace-nowrap transition-all duration-200 ease-out">
                                {height}m
                              </span>
                              <span className="flex items-center overflow-hidden transition-all duration-200 ease-out group-hover:w-5 w-0 ml-0">
                                <button
                                  className="translate-x-2 group-hover:translate-x-0 opacity-0 group-hover:opacity-100 transition-all duration-200 ease-out p-1 rounded hover:bg-red-50 text-red-600"
                                  title="Remove equipment"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setConfirmDeleteId(eq.id);
                                  }}
                                >
                                  <Trash2 size={12} />
                                </button>
                              </span>
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>

        </div>
      </div>

      {/* Confirm Delete Dialog */}
      {confirmDeleteId && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onMouseDown={() => setConfirmDeleteId(null)} />
          <div className="relative bg-white w-full max-w-sm mx-4 rounded-lg shadow-2xl border border-gray-200 overflow-hidden" onMouseDown={(e)=>e.stopPropagation()}>
            <div className="p-4 border-b flex items-center">
              <div className="w-8 h-8 mr-2 rounded-full bg-red-100 text-red-600 flex items-center justify-center">
                <Trash2 size={16} />
              </div>
              <h4 className="text-sm font-semibold text-gray-800">Remove equipment?</h4>
            </div>
            <div className="p-4 text-sm text-gray-600">
              <p className="mb-2">This will remove <span className="font-medium text-gray-900">{currentSite.equipment.find(e=>e.id===confirmDeleteId)?.equipment.name}</span> from the tower.</p>
              <p>You can always add it again later.</p>
            </div>
            <div className="p-4 bg-gray-50 flex items-center justify-end space-x-2">
              <button
                type="button"
                onClick={(e)=>{e.preventDefault(); e.stopPropagation(); setConfirmDeleteId(null);}}
                className="px-3 py-1.5 text-sm rounded border border-gray-300 text-gray-700 hover:bg-white"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={(e)=>{e.preventDefault(); e.stopPropagation(); removeEquipment(confirmDeleteId); setConfirmDeleteId(null);}}
                className="px-3 py-1.5 text-sm rounded bg-red-600 text-white hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Site List Sidebar - Overlay */}
      <div ref={sidebarRef}>
        <SiteListSidebar
          isOpen={isSiteListOpen}
          onClose={() => setSiteListOpen(false)}
          onCreateNew={() => {
            setSiteListOpen(false);
            // Open New Site modal
            window.dispatchEvent(new Event('openSiteModal'));
          }}
        />
      </div>
    </div>
  );
};

export default TowerView3D;
